import { useMemo } from 'react';

import type { SymbolKey } from '@/entities/symbol';
import { symbolKey } from '@/entities/symbol';
import { useSpotCatalog } from '@/hooks/api/catalog';
import type { CatalogItem } from '@/pages/Catalog/types';

type CatalogIndex = Map<string, CatalogItem>;

export type CatalogSymbolItem = {
  id: string;
  alias: string;
  code: string;
  currency: string;
  units: string;
  title: string;
  period: string;
  symbol: SymbolKey;
};

const getSymbolMatchKey = (symbol: SymbolKey) => {
  return `${symbol.Code}-${symbol.PeriodType}-${symbol.TimeRef}`;
};

const buildCatalogIndex = (catalog: CatalogItem[]): CatalogIndex => {
  const index = new Map<string, CatalogItem>();
  for (const item of catalog) {
    index.set(getSymbolMatchKey(item.Symbol), item);
  }
  return index;
};

export const useCatalogSymbolItems = (symbols: SymbolKey[]) => {
  const { data: catalog, isLoading } = useSpotCatalog();

  const catalogIndex = useMemo(() => {
    if (!catalog) return null;
    return buildCatalogIndex(catalog);
  }, [catalog]);

  const items: CatalogSymbolItem[] = useMemo(() => {
    if (!catalogIndex) return [];

    return symbols.map(symbol => {
      const catalogItem = catalogIndex.get(getSymbolMatchKey(symbol));

      return {
        id: symbolKey(symbol),
        alias: catalogItem?.Alias || symbol.Code,
        code: symbol.Code,
        currency: catalogItem?.Currency || '',
        units: catalogItem?.Units || '',
        title: catalogItem?.Title || '',
        period: symbol.Period,
        symbol
      };
    });
  }, [catalogIndex, symbols]);

  return { items, isLoading: Boolean(isLoading) };
};
